import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ─── Satellite monitoring / scan frequency settings ───────────────────────────

export type ScanFrequency = 'daily' | 'every_3_days' | 'weekly' | 'fortnightly' | 'manual_only';
export type ScanTimeWindow = '00:00' | '03:00' | '06:00' | '12:00' | '18:00';
export type ScanCloudCoverLimit = 10 | 20 | 30 | 40 | 50;

export interface OrgSatelliteMonitoringSettings {
  /** How often the automated satellite sweep runs for this organisation */
  frequency: ScanFrequency;
  /** Preferred UTC hour for the automated sweep */
  preferred_utc_hour: ScanTimeWindow;
  /** Skip a boundary if it was already swept within this many days */
  rescan_suppress_days: number;
  /** Maximum acceptable cloud-cover percentage (0–100) for a Sentinel-2 image to be used */
  max_cloud_cover: ScanCloudCoverLimit;
  /** Whether operators can manually trigger a sweep from the Anomaly Alerts UI */
  allow_manual_trigger: boolean;
  /** Verticals to include in this org's automated sweep (empty = all) */
  verticals: string[];
}

export const DEFAULT_SATELLITE_MONITORING: OrgSatelliteMonitoringSettings = {
  frequency:            'weekly',
  preferred_utc_hour:   '03:00',
  rescan_suppress_days: 6,
  max_cloud_cover:      20,
  allow_manual_trigger: true,
  verticals:            [],
};

// ─── Drone image pipeline settings ───────────────────────────────────────────

export type ImagePipelineMode = 'upload_processed' | 'upload_raw' | 'upload_both';
export type ImageProcessingEngine = 'external' | 'odm' | 'platform';
export type RawImageFormat = 'jpeg' | 'dng' | 'tiff';

export interface OrgImagePipelineSettings {
  /** Whether the org uploads pre-processed outputs, raw images, or both */
  default_mode: ImagePipelineMode;
  /** Where photogrammetric processing happens when raw images are uploaded */
  processing_engine: ImageProcessingEngine;
  /** Raw image format coming off the drone SD card */
  raw_format: RawImageFormat;
  /** Whether operators can override the org default per mission */
  allow_per_mission_override: boolean;
}

export const DEFAULT_IMAGE_PIPELINE: OrgImagePipelineSettings = {
  default_mode:               'upload_processed',
  processing_engine:          'external',
  raw_format:                 'jpeg',
  allow_per_mission_override: true,
};

// ─── UoM / ROI settings — mirrored from backend OrgUomSettings ───────────────
export interface OrgUomSettings {
  crude_price_per_unit: number;
  fine_per_incident: number;
  survey_cost_per_mission: number;
  volume_unit: 'bbl' | 'm3' | 'kL' | 'L';
  volume_unit_label: string;
  ch4_to_co2e_factor: number;
  mass_rate_unit: 'kg/hr' | 'lb/hr' | 'tonne/day';
  mass_rate_unit_label: string;
  area_unit: 'ha' | 'acres' | 'm2';
  area_unit_label: string;
  currency_symbol: string;
  currency_code: string;
}

export const DEFAULT_UOM: OrgUomSettings = {
  crude_price_per_unit:    82,
  fine_per_incident:       85_000,
  survey_cost_per_mission: 1_200,
  volume_unit:             'bbl',
  volume_unit_label:       'bbl',
  ch4_to_co2e_factor:      29.8,
  mass_rate_unit:          'kg/hr',
  mass_rate_unit_label:    'kg/hr',
  area_unit:               'ha',
  area_unit_label:         'ha',
  currency_symbol:         '$',
  currency_code:           'USD',
};

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  organization?: string;
}

interface Organization {
  id: string;
  name: string;
  subdomain: string;
  role: string;
}

// ─── Remember-me flag key ─────────────────────────────────────────────────────
const REMEMBER_ME_KEY = 'nabhos-remember-me';

/**
 * Returns localStorage when the user chose "Remember Me", otherwise
 * sessionStorage so the session is cleared when the browser is closed.
 */
const getAuthStorage = (): Storage =>
  typeof window !== 'undefined' && localStorage.getItem(REMEMBER_ME_KEY) === 'true'
    ? localStorage
    : sessionStorage;

/**
 * Custom StateStorage proxy that dynamically routes getItem / setItem / removeItem
 * to localStorage or sessionStorage depending on REMEMBER_ME_KEY at runtime.
 */
const dynamicStateStorage = {
  getItem: (name: string) => getAuthStorage().getItem(name),
  setItem: (name: string, value: string) => getAuthStorage().setItem(name, value),
  removeItem: (name: string) => getAuthStorage().removeItem(name),
};

const dynamicAuthStorage = createJSONStorage<AuthState>(() => dynamicStateStorage);

// ─── Auth state ───────────────────────────────────────────────────────────────
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  // V0.2 Multi-tenant support
  currentOrganizationId: string | null;
  organizations: Organization[];
  permissions: string[];
  /** Module keys enabled for the current organisation (e.g. 'mvp1_agrotech') */
  organizationModules: string[];

  /** Resolved UoM / ROI settings for the current organisation */
  orgUomSettings: OrgUomSettings;

  /** Drone image pipeline settings for the current organisation */
  orgImagePipeline: OrgImagePipelineSettings;

  /** Satellite monitoring / scan frequency settings for the current organisation */
  orgSatelliteMonitoring: OrgSatelliteMonitoringSettings;

  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<User>) => void;

  // V0.2 Multi-tenant methods
  setOrganizations: (organizations: Organization[]) => void;
  setCurrentOrganization: (organizationId: string) => void;
  setPermissions: (permissions: string[]) => void;
  setOrganizationModules: (modules: string[]) => void;
  setOrgUomSettings: (settings: Partial<OrgUomSettings>) => void;
  setOrgImagePipeline: (settings: Partial<OrgImagePipelineSettings>) => void;
  setOrgSatelliteMonitoring: (settings: Partial<OrgSatelliteMonitoringSettings>) => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;

  /** Write or remove the nabhos-remember-me flag in localStorage. */
  setRememberMe: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      currentOrganizationId: null,
      organizations: [],
      permissions: [],
      organizationModules: ['mvp0_core', 'mvp0_enterprise'],
      orgUomSettings:         { ...DEFAULT_UOM },
      orgImagePipeline:       { ...DEFAULT_IMAGE_PIPELINE },
      orgSatelliteMonitoring: { ...DEFAULT_SATELLITE_MONITORING },

      setAuth: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
        }),
      
      clearAuth: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          currentOrganizationId: null,
          organizations: [],
          permissions: [],
          organizationModules: ['mvp0_core', 'mvp0_enterprise'],
          orgUomSettings:         { ...DEFAULT_UOM },
          orgImagePipeline:       { ...DEFAULT_IMAGE_PIPELINE },
          orgSatelliteMonitoring: { ...DEFAULT_SATELLITE_MONITORING },
        }),
      
      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),
      
      // V0.2 Multi-tenant methods
      setOrganizations: (organizations) =>
        set({ organizations }),
      
      setCurrentOrganization: (organizationId) =>
        set({ currentOrganizationId: organizationId }),

      setPermissions: (permissions) =>
        set({ permissions }),

      setOrganizationModules: (modules) =>
        set({ organizationModules: modules }),

      setOrgUomSettings: (settings) =>
        set((state) => ({
          orgUomSettings: { ...state.orgUomSettings, ...settings },
        })),

      setOrgImagePipeline: (settings) =>
        set((state) => ({
          orgImagePipeline: { ...state.orgImagePipeline, ...settings },
        })),

      setOrgSatelliteMonitoring: (settings) =>
        set((state) => ({
          orgSatelliteMonitoring: { ...state.orgSatelliteMonitoring, ...settings },
        })),

      hasPermission: (permission) => {
        const { permissions } = get();
        // Wildcard permission grants all access
        if (permissions.includes('*.*')) return true;
        return permissions.includes(permission);
      },
      
      hasAnyPermission: (requiredPermissions) => {
        const { permissions } = get();
        // Wildcard permission grants all access
        if (permissions.includes('*.*')) return true;
        return requiredPermissions.some(p => permissions.includes(p));
      },
      
      hasAllPermissions: (requiredPermissions) => {
        const { permissions } = get();
        // Wildcard permission grants all access
        if (permissions.includes('*.*')) return true;
        return requiredPermissions.every(p => permissions.includes(p));
      },

      setRememberMe: (value) => {
        if (value) {
          localStorage.setItem(REMEMBER_ME_KEY, 'true');
        } else {
          localStorage.removeItem(REMEMBER_ME_KEY);
        }
      },
    }),
    {
      name:    'nabhos-auth',
      storage: dynamicAuthStorage,
      onRehydrateStorage: () => (_state, _error) => {
        // If nabhos-auth exists in localStorage but no nabhos-remember-me flag
        // is set, this is old data written before the dynamic-storage migration.
        // Clear it and force re-login.
        if (
          localStorage.getItem('nabhos-auth') !== null &&
          localStorage.getItem(REMEMBER_ME_KEY) === null
        ) {
          useAuthStore.getState().clearAuth();
          localStorage.removeItem('nabhos-auth');
        }
      },
    }
  )
);

// Made with Bob
